import React, {createContext, ReactNode, useContext, useMemo} from 'react';

export type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

/** A suggestion shown on an empty conversation; clicking it sends the prompt. */
export interface StarterPrompt {
    /** Short chip text. */
    label: string;
    /** The message sent on the user's behalf. */
    prompt: string;
}

export interface AssistantIntegration {
    /** Fetch wrapper that attaches the bearer token (host-provided). */
    authenticatedFetch: AuthenticatedFetch;
    /** Access token factory for the SignalR connection (host-provided). */
    accessTokenFactory: () => string | null | Promise<string | null>;
    /** API origin, e.g. https://localhost:7272 */
    baseUrl: string;
    /**
     * Stable id of the current user (e.g. person id) used to scope persisted
     * UI preferences such as the panel's expanded/collapsed state.
     */
    userStorageKey?: string | null;
    /**
     * Fired when a live tool-call item completes successfully in the chat
     * stream. The host can map tool names to cache invalidations so open
     * pages refresh after an assistant edits app data (see
     * api/assistantToolRefresh.ts). Fires only for live SignalR events —
     * snapshot catch-up after a reconnect does not replay completions.
     */
    onToolCallCompleted?: (toolName: string) => void;
    /**
     * Fired when the assistant's navigate_to tool completes in a live run,
     * with the in-app path to open (always starts with "/"). The host routes
     * there; the conversation stays open. Omit it and the tool's link is
     * simply shown in the reply.
     */
    onNavigate?: (path: string) => void;
    /**
     * A React hook (called on every panel render) returning the suggestions to
     * offer on an empty conversation: the host knows the page and the user's
     * role. Must be the same function for the integration's lifetime. Omit it
     * and an empty conversation shows nothing.
     */
    useStarterPrompts?: () => StarterPrompt[];
}

const AssistantIntegrationContext = createContext<
    AssistantIntegration | undefined
>(undefined);

/**
 * The assistant module's single seam to the host app: authentication + API origin.
 * Everything inside src/assistant depends only on this provider, so the module
 * lifts into a sister app by wrapping it with that app's auth primitives.
 */
export const AssistantIntegrationProvider: React.FC<{
    integration: AssistantIntegration;
    children: ReactNode;
}> = ({integration, children}) => {
    const value = useMemo(() => integration, [integration]);

    return (
        <AssistantIntegrationContext.Provider value={value}>
            {children}
        </AssistantIntegrationContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAssistantIntegration = (): AssistantIntegration => {
    const context = useContext(AssistantIntegrationContext);
    if (context === undefined) {
        throw new Error(
            'useAssistantIntegration must be used within an AssistantIntegrationProvider'
        );
    }

    return context;
};
